import { AMIDataPoint, Equipment, Project } from '../types/project';
import { canRunTraditional } from './calc/runTraditional';
import { existingLoads } from './calc/loadRoles';

// What the Results lock screen needs to know before it offers the free trial,
// the $30 unlock or a subscription: whether either existing-load path can
// produce a result, and how to describe that readiness above the buttons.
// Kept pure so the gate is unit tested without a render. Until 2026-09-02 the
// screen counted only the measured path, so a contractor without a
// subscription could never unlock a traditional-only project — it asked for
// utility data the method does not need and offered no control at all.

export interface LockScreenReadiness {
  /** Either path can produce a result. False = nothing to unlock yet. */
  hasData: boolean;
  /** The path the badge describes. Measured wins when both are live — it is
   * the product's headline and the number the report will lead with. */
  path: 'measured' | 'traditional' | null;
  headline: string;
  /** Traditional only: attestation state and the count of untouched standard
   * values — the pre-paywall analogue of the AMI point count, so the preparer
   * sees what the report will disclose before paying. */
  detail: string | null;
}

/** Interval data on the project, or a manually entered peak. */
export function measuredPathReady(project: Partial<Project>, amiData: AMIDataPoint[]): boolean {
  return (
    amiData.length > 0 ||
    (project.max_recorded_demand_kw != null && project.max_recorded_demand_kw > 0)
  );
}

export function lockScreenReadiness(
  project: Partial<Project>,
  amiData: AMIDataPoint[],
  equipment: Equipment[] = []
): LockScreenReadiness {
  if (measuredPathReady(project, amiData)) {
    return {
      hasData: true,
      path: 'measured',
      headline: 'Data verified — ready to unlock',
      detail: null,
    };
  }

  // Floor area entered AND inside the section's scope (calc/eligibility.ts):
  // an ineligible or unanswered project has nothing to unlock on this path.
  if (canRunTraditional(project)) {
    const inventory = existingLoads(equipment);
    const defaults = inventory.filter((eq) => eq.value_source === 'default').length;
    const parts = [
      project.inventory_attested === true ? 'Inventory attested' : 'Inventory not yet attested',
    ];
    if (inventory.length === 0) {
      parts.push('no fixed appliances listed');
    } else if (defaults > 0) {
      parts.push(`${defaults} of ${inventory.length} inventory values are standard defaults`);
    } else {
      parts.push('every inventory value entered from a nameplate');
    }
    return {
      hasData: true,
      path: 'traditional',
      headline: 'Dwelling profile complete — ready to unlock',
      detail: parts.join(' · '),
    };
  }

  return { hasData: false, path: null, headline: '', detail: null };
}
